import { Link } from 'react-router-dom';

function TermsOfService() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gray-900 text-white py-6">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-grass-500 to-grass-600 rounded-xl flex items-center justify-center">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
            </div>
            <span className="text-xl font-bold">TurfPilot</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container-custom px-4 sm:px-6 lg:px-8 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          Terms of Service
        </h1>
        <p className="text-gray-500 mb-8">Last Updated: January 18, 2025</p>

        <div className="prose prose-gray max-w-none">
          <p className="text-lg text-gray-600 mb-8">
            Welcome to TurfPilot. These Terms of Service (&quot;Terms&quot;)
            govern your access to and use of the TurfPilot mobile application,
            website, and related services (collectively, the
            &quot;Service&quot;) provided by ACID Engineering LLC, a Florida
            limited liability company doing business as TurfPilot
            (&quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; or
            &quot;our&quot;).
          </p>

          <p className="text-gray-600 mb-8">
            Please read these Terms carefully before using the Service. By
            accessing or using the Service, you agree to be bound by these Terms
            and our Privacy Policy. If you do not agree to these Terms, you may
            not access or use the Service.
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              1. Acceptance of Terms
            </h2>
            <p className="text-gray-600 mb-4">
              By creating an account, accessing, or using the Service, you
              acknowledge that you have read, understood, and agree to be bound
              by these Terms. If you are using the Service on behalf of an
              organization, you represent and warrant that you have the
              authority to bind that organization to these Terms.
            </p>
            <p className="text-gray-600">
              You must be at least 18 years old to use this Service. By using
              the Service, you represent and warrant that you are at least 18
              years of age and have the legal capacity to enter into these
              Terms.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              2. Description of Service
            </h2>
            <p className="text-gray-600 mb-4">
              TurfPilot is an AI-powered lawn care management application that
              provides:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Personalized lawn care plans and schedules</li>
              <li>AI-powered lawn photo analysis and recommendations</li>
              <li>Weather-based task reminders and notifications</li>
              <li>Lawn health tracking and progress monitoring</li>
              <li>Product recommendations for lawn care</li>
              <li>Expert guidance through AI-powered chat</li>
            </ul>
            <p className="text-gray-600 mt-4">
              We reserve the right to modify, suspend, or discontinue any part
              of the Service at any time, with or without notice.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              3. Account Registration and Security
            </h2>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              3.1 Account Creation
            </h3>
            <p className="text-gray-600 mb-4">
              To use certain features of the Service, you must create an
              account. You may register using your Google account or other
              methods we make available. You agree to provide accurate, current,
              and complete information during registration.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              3.2 Account Security
            </h3>
            <p className="text-gray-600 mb-4">
              You are responsible for maintaining the confidentiality of your
              account credentials and for all activities that occur under your
              account. You agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>
                Notify us immediately of any unauthorized use of your account
              </li>
              <li>
                Ensure that you log out from your account at the end of each
                session
              </li>
              <li>Not share your account credentials with any third party</li>
            </ul>
            <p className="text-gray-600 mt-4">
              We are not liable for any loss or damage arising from your failure
              to protect your account credentials.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              3.3 Account Termination
            </h3>
            <p className="text-gray-600">
              You may delete your account at any time through the Service
              settings. We reserve the right to suspend or terminate your
              account if you violate these Terms or engage in any activity that
              we determine, in our sole discretion, to be harmful to the Service
              or other users.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              4. User Content
            </h2>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              4.1 Your Content
            </h3>
            <p className="text-gray-600 mb-4">
              The Service allows you to upload, submit, and store content
              including photos, text, observations, and other materials
              (&quot;User Content&quot;). You retain ownership of your User
              Content.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              4.2 License Grant
            </h3>
            <p className="text-gray-600 mb-4">
              By uploading User Content, you grant us a non-exclusive,
              worldwide, royalty-free, sublicensable license to use, reproduce,
              modify, adapt, process, and display your User Content solely for
              the purpose of providing and improving the Service. This license
              includes the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>
                Process your photos using AI to provide lawn analysis and
                recommendations
              </li>
              <li>Store and backup your content on our servers</li>
              <li>
                Use anonymized or aggregated data to improve our AI models and
                Service
              </li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              4.3 Content Responsibility
            </h3>
            <p className="text-gray-600">
              You are solely responsible for your User Content and represent
              that you have all rights necessary to grant the license above. You
              agree not to upload content that infringes on intellectual
              property rights, violates any law, or is otherwise objectionable.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              5. Subscriptions and Payments
            </h2>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              5.1 Subscription Plans
            </h3>
            <p className="text-gray-600 mb-4">
              The Service may offer free and paid subscription plans. Paid plans
              provide access to additional features and capabilities.
              Subscription details, including pricing and features, are
              available within the Service.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              5.2 Billing
            </h3>
            <p className="text-gray-600 mb-4">
              For paid subscriptions, you agree to pay all applicable fees.
              Subscriptions automatically renew unless cancelled before the
              renewal date. You authorize us to charge your payment method for
              recurring subscription fees.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              5.3 Cancellation and Refunds
            </h3>
            <p className="text-gray-600">
              You may cancel your subscription at any time. Cancellation will
              take effect at the end of your current billing period. We do not
              provide refunds for partial billing periods unless required by
              applicable law.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              6. AI-Generated Content and Recommendations
            </h2>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              6.1 Nature of AI Recommendations
            </h3>
            <p className="text-gray-600 mb-4">
              The Service uses artificial intelligence to analyze lawn
              conditions and provide recommendations. These AI-generated
              recommendations are provided for informational and educational
              purposes only and should not be considered professional advice.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              6.2 Limitations
            </h3>
            <p className="text-gray-600 mb-4">
              AI-generated content may contain errors, inaccuracies, or may not
              be suitable for your specific situation. You acknowledge and agree
              that:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>
                AI recommendations are not guaranteed to be accurate or complete
              </li>
              <li>
                Results may vary based on numerous factors outside our control
              </li>
              <li>
                You should exercise your own judgment before acting on any
                recommendation
              </li>
              <li>
                For serious lawn problems, you should consult a professional
                lawn care specialist
              </li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              6.3 Product Recommendations
            </h3>
            <p className="text-gray-600">
              The Service may recommend specific products. These recommendations
              are based on our analysis and are not endorsements. We are not
              responsible for the quality, safety, or effectiveness of any
              third-party products. Always follow product label instructions and
              applicable regulations.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              7. Prohibited Uses
            </h2>
            <p className="text-gray-600 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>
                Use the Service for any unlawful purpose or in violation of any
                applicable laws
              </li>
              <li>
                Attempt to gain unauthorized access to any part of the Service
                or related systems
              </li>
              <li>
                Interfere with or disrupt the Service or servers connected to
                the Service
              </li>
              <li>
                Use any automated means to access the Service without our
                express permission
              </li>
              <li>
                Reverse engineer, decompile, or disassemble any part of the
                Service
              </li>
              <li>
                Remove or alter any proprietary notices or labels on the Service
              </li>
              <li>
                Use the Service to transmit malware, viruses, or other harmful
                code
              </li>
              <li>
                Impersonate any person or entity or misrepresent your
                affiliation
              </li>
              <li>
                Collect or harvest information about other users without consent
              </li>
              <li>
                Use the Service in any manner that could damage, disable, or
                impair the Service
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              8. Intellectual Property
            </h2>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              8.1 Our Intellectual Property
            </h3>
            <p className="text-gray-600 mb-4">
              The Service, including its original content, features,
              functionality, design, and underlying technology, is owned by ACID
              Engineering LLC and is protected by copyright, trademark, and
              other intellectual property laws. The TurfPilot name, logo, and
              related marks are trademarks of ACID Engineering LLC.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              8.2 Limited License
            </h3>
            <p className="text-gray-600">
              Subject to these Terms, we grant you a limited, non-exclusive,
              non-transferable, revocable license to access and use the Service
              for your personal, non-commercial use. This license does not
              include the right to copy, modify, distribute, sell, or lease any
              part of the Service.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              9. Third-Party Services and Links
            </h2>
            <p className="text-gray-600">
              The Service may contain links to third-party websites or services
              that are not owned or controlled by us. We are not responsible for
              the content, privacy policies, or practices of any third-party
              websites or services. You acknowledge and agree that we shall not
              be liable for any damage or loss caused by your use of any
              third-party content, products, or services.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              10. Disclaimers
            </h2>

            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <p className="text-gray-700 font-medium">
                THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS
                AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
                OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
                NON-INFRINGEMENT.
              </p>
            </div>

            <p className="text-gray-600 mb-4">We do not warrant that:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>The Service will meet your specific requirements</li>
              <li>
                The Service will be uninterrupted, timely, secure, or error-free
              </li>
              <li>
                The results obtained from using the Service will be accurate or
                reliable
              </li>
              <li>Any errors in the Service will be corrected</li>
              <li>
                Following our recommendations will produce desired lawn care
                results
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              11. Limitation of Liability
            </h2>

            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <p className="text-gray-700 font-medium">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
                SHALL ACID ENGINEERING LLC, ITS OFFICERS, DIRECTORS, EMPLOYEES,
                OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION
                LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE
                LOSSES, RESULTING FROM:
              </p>
            </div>

            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>
                Your access to or use of (or inability to access or use) the
                Service
              </li>
              <li>Any conduct or content of any third party on the Service</li>
              <li>Any content obtained from the Service</li>
              <li>
                Unauthorized access, use, or alteration of your transmissions or
                content
              </li>
              <li>
                Any damage to your lawn, property, or plants resulting from
                following recommendations provided by the Service
              </li>
            </ul>

            <p className="text-gray-600 mt-4">
              In no event shall our total liability to you for all claims exceed
              the amount you paid us, if any, in the twelve (12) months
              preceding the claim.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              12. Indemnification
            </h2>
            <p className="text-gray-600">
              You agree to indemnify, defend, and hold harmless ACID Engineering
              LLC and its officers, directors, employees, agents, and successors
              from and against any claims, liabilities, damages, losses, and
              expenses, including reasonable attorney&apos;s fees, arising out
              of or related to your use of the Service, your violation of these
              Terms, or your violation of any rights of another.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              13. Governing Law and Dispute Resolution
            </h2>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              13.1 Governing Law
            </h3>
            <p className="text-gray-600 mb-4">
              These Terms shall be governed by and construed in accordance with
              the laws of the State of Florida, United States, without regard to
              its conflict of law provisions.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              13.2 Dispute Resolution
            </h3>
            <p className="text-gray-600 mb-4">
              Any dispute arising from or relating to these Terms or the Service
              shall first be attempted to be resolved through informal
              negotiation. If the dispute cannot be resolved informally within
              30 days, either party may pursue resolution through binding
              arbitration administered by the American Arbitration Association
              in accordance with its Commercial Arbitration Rules.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              13.3 Class Action Waiver
            </h3>
            <p className="text-gray-600">
              You agree that any dispute resolution proceedings will be
              conducted only on an individual basis and not in a class,
              consolidated, or representative action. You waive any right to
              participate in a class action lawsuit or class-wide arbitration.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              14. Changes to Terms
            </h2>
            <p className="text-gray-600">
              We reserve the right to modify these Terms at any time. We will
              provide notice of material changes by posting the updated Terms on
              this page and updating the &quot;Last Updated&quot; date. For
              significant changes, we may also notify you via email or through
              the Service. Your continued use of the Service after any changes
              constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              15. General Provisions
            </h2>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              15.1 Entire Agreement
            </h3>
            <p className="text-gray-600 mb-4">
              These Terms, together with our Privacy Policy, constitute the
              entire agreement between you and ACID Engineering LLC regarding
              the Service and supersede all prior agreements.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              15.2 Severability
            </h3>
            <p className="text-gray-600 mb-4">
              If any provision of these Terms is found to be unenforceable or
              invalid, that provision will be limited or eliminated to the
              minimum extent necessary, and the remaining provisions will remain
              in full force and effect.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              15.3 Waiver
            </h3>
            <p className="text-gray-600 mb-4">
              Our failure to enforce any right or provision of these Terms will
              not be considered a waiver of those rights.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">
              15.4 Assignment
            </h3>
            <p className="text-gray-600">
              You may not assign or transfer these Terms without our prior
              written consent. We may assign our rights and obligations under
              these Terms without restriction.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              16. Contact Us
            </h2>
            <p className="text-gray-600 mb-4">
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-800 font-medium">
                ACID Engineering LLC (d/b/a TurfPilot)
              </p>
              <p className="text-gray-600 mt-2">
                Email:{' '}
                <a
                  href="mailto:legal@turfpilot.app"
                  className="text-grass-600 hover:text-grass-700"
                >
                  legal@turfpilot.app
                </a>
              </p>
            </div>
          </section>
        </div>

        {/* Back to home */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/"
            className="inline-flex items-center text-grass-600 hover:text-grass-700 font-medium"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Home
          </Link>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 mt-12">
        <div className="container-custom px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm">
            &copy; {new Date().getFullYear()} TurfPilot. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default TermsOfService;
